import React, { useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaHeart, FaShoppingCart } from "react-icons/fa";
import { base_url } from "../utils/axiosConfig";
import { toast } from "react-toastify";
import { CurrencyContext } from "../features/context/CurrencyContext";

const SubCategoryPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    category,
    subCategory,
    subSubCategory,
    subSubSubCategory,
    currentCategory,
  } = location.state || {};

  const { convertPrice, getCurrencySymbol } = useContext(CurrencyContext);

  const token = localStorage.getItem("user_website")
    ? JSON.parse(localStorage.getItem("user_website"))
    : null;

  const addToWishlist = async (productId, price) => {
    try {
      const response = await fetch(`${base_url}wishlist/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token?.token}`,
        },
        body: JSON.stringify({
          productId,
          quantity: 1,
          price,
        }),
      });
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      toast.success(data.message);
    } catch (error) {
      console.error("Error adding to wishlist:", error);
      toast.error("Failed to add to wishlist.");
    }
  };

  const addToCart = async (productId, subCategoryId, categoryId, price) => {
    try {
      const isWalletUser = token?.data?.wallet;
      const cartUrl = isWalletUser
        ? `${base_url}cardAddItem/member`
        : `${base_url}cartAddItem`;

      const cartBody = {
        productId,
        subCategoryId,
        categoryId,
        quantity: 1,
      };

      const response = await fetch(cartUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token?.token}`,
        },
        body: JSON.stringify(cartBody),
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      toast.success("Added to cart!");
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Access denied, not an end user");
    }
  };

  const displayCategory =
    currentCategory || subCategory || subSubCategory || subSubSubCategory;

  const activeProducts =
    displayCategory?.products?.filter(
      (product) => product?.isActive !== false
    ) || [];

  return (
    <div className="w-full min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-20">
        {/* Category Title and Description */}
        <div className="mb-12 text-center md:text-left">
          <h4 className="text-3xl md:text-4xl font-semibold text-gray-800 mb-4">
            Products for{" "}
            {displayCategory?.subCategoryName ||
              displayCategory?.subSubCategoryName ||
              displayCategory?.subSubSubCategoryName ||
              displayCategory?.categoryName ||
              "Category"}
          </h4>
          <p className="text-gray-600 text-base md:text-lg max-w-3xl mx-auto md:mx-0">
            {displayCategory?.description ||
              "Explore our curated selection of products tailored to your needs."}
          </p>
        </div>

        {/* Products Section */}
        <div className="mb-8">
          {activeProducts.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-6">
              {activeProducts.map((product, index) => (
                <div
                  key={index}
                  className="flex flex-col items-center text-center bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 p-4 border border-gray-100 cursor-pointer"
                  onClick={() =>
                    navigate(`/product-details/${product?._id}`, {
                      state: {
                        product,
                        category,
                        subCategory,
                        subSubCategory,
                        subSubSubCategory,
                        currentCategory,
                      },
                    })
                  }
                >
                  {/* Product Image Container */}
                  <div className="product-img-container w-full h-48 sm:h-56 md:h-64 flex items-center justify-center relative group">
                    <img
                      src={
                        product?.variations?.[0]?.images[0] ||
                        product?.images?.[0]
                      }
                      alt={product?.productName}
                      className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="action-overlay">
                      <button
                        className="action-btn"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent navigating to product details
                          addToWishlist(
                            product?._id,
                            product?.price?.amount ||
                              product?.variations?.[0]?.price?.amount ||
                              product?.cost?.min
                          );
                        }}
                        title="Add to Wishlist"
                      >
                        <FaHeart size={18} className="text-red-500" />
                      </button>
                      <button
                        className="action-btn"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent navigating to product details
                          addToCart(
                            product?._id,
                            subCategory?._id ||
                              subSubCategory?._id ||
                              subSubSubCategory?._id,
                            category?._id,
                            product?.price?.amount ||
                              product?.variations?.[0]?.price?.amount ||
                              product?.cost?.min
                          );
                        }}
                        title="Add to Cart"
                      >
                        <FaShoppingCart size={18} className="text-green-500" />
                      </button>
                    </div>
                  </div>
                  {/* Product Details */}
                  <div className="mt-3 w-full flex flex-col gap-1">
                    <div className="font-medium text-base md:text-lg text-[#303030] text-start line-clamp-2 cursor-pointer hover:text-[#35A93E] transition-colors">
                      {product?.productName}
                    </div>
                    <div className="text-gray-500 text-sm md:text-base text-start line-clamp-1">
                      {product?.briefDescription?.slice(0, 30)}...
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="font-bold text-base md:text-lg text-[#303030] text-start">
                        {getCurrencySymbol()}
                        {convertPrice(
                          product?.price?.amount ||
                            product?.variations?.[0]?.price?.amount ||
                            product?.cost?.min
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-base md:text-lg text-center">
              No products available for this category.
            </p>
          )}
        </div>

        <style>
          {`
            /* Base Styles */
            .product-img-container {
              position: relative;
              overflow: hidden;
              border-radius: 8px;
              background-color: transparent;
            }
            .product-img-container img {
              width: 100%;
              height: 100%;
              transition: transform 0.3s ease;
            }
            .product-img-container:hover img {
              transform: scale(1.05);
            }
            .product-img-container:hover .action-overlay {
              opacity: 1;
            }
            .action-overlay {
              position: absolute;
              inset: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 12px;
              background: rgba(0, 0, 0, 0.2);
              opacity: 0;
              transition: opacity 0.3s ease;
            }
            .action-btn {
              padding: 8px;
              background: white;
              border-radius: 50%;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
              transition: background-color 0.2s ease;
            }
            .action-btn:hover {
              background: #f0f0f0;
            }
            .add-to-cart-btn {
              background: #FF8500;
              color: white;
              padding: 8px 16px;
              border-radius: 8px;
              font-size: 14px;
              font-weight: 600;
              transition: background-color 0.2s ease;
              width: 100%;
              text-align: center;
            }
            .add-to-cart-btn:hover {
              background: #e07600;
            }
            .grayscale {
              filter: grayscale(100%);
            }

            /* Tablet (577px - 768px) */
            @media (max-width: 768px) {
              .max-w-3xl {
                max-width: 32rem !important; /* max-w-2xl */
              }
              .text-3xl {
                font-size: 1.75rem !important;
              }
              .text-4xl {
                font-size: 2rem !important;
              }
              .text-lg {
                font-size: 1rem !important;
              }
              .text-xl {
                font-size: 1.125rem !important;
              }
              .text-2xl {
                font-size: 1.25rem !important;
              }
              .text-base {
                font-size: 0.875rem !important;
              }
              .text-sm {
                font-size: 0.75rem !important;
              }
              .h-48 {
                height: 12rem !important;
              }
              .h-64 {
                height: 16rem !important;
              }
              .h-80 {
                height: 18rem !important;
              }
              .h-56 {
                height: 14rem !important;
              }
              .h-64 {
                height: 16rem !important;
              }
              .my-8 {
                margin-top: 1.5rem !important;
                margin-bottom: 1.5rem !important;
              }
              .action-overlay {
                gap: 8px;
              }
              .action-btn {
                padding: 6px;
              }
              .action-btn svg {
                font-size: 16px;
              }
              .add-to-cart-btn {
                font-size: 12px;
                padding: 6px 12px;
              }
            }

            /* Mobile (up to 576px) */
            @media (max-width: 576px) {
              .max-w-3xl {
                max-width: 24rem !important; /* max-w-xl */
              }
              .text-3xl {
                font-size: 1.5rem !important;
              }
              .text-4xl {
                font-size: 1.75rem !important;
              }
              .text-lg {
                font-size: 0.875rem !important;
              }
              .text-xl {
                font-size: 1rem !important;
              }
              .text-2xl {
                font-size: 1.125rem !important;
              }
              .text-base {
                font-size: 0.75rem !important;
              }
              .text-sm {
                font-size: 0.675rem !important;
              }
              .h-48 {
                height: 10rem !important;
              }
              .h-64 {
                height: 12rem !important;
              }
              .h-80 {
                height: 14rem !important;
              }
              .h-56 {
                height: 12rem !important;
              }
              .h-64 {
                height: 14rem !important;
              }
              .my-8 {
                margin-top: 1rem !important;
                margin-bottom: 1rem !important;
              }
              .action-overlay {
                gap: 6px;
              }
              .action-btn {
                padding: 5px;
              }
              .action-btn svg {
                font-size: 14px;
              }
              .add-to-cart-btn {
                font-size: 11px;
                padding: 5px 10px;
              }
              .text-center {
                text-align: center !important;
              }
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default SubCategoryPage;
